<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
    <!-- Navigation Header -->
    <header class="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-20">
          <!-- Logo -->
          <div class="flex items-center">
            <router-link to="/" class="flex items-center space-x-3 group">
              <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform">
                <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy" class="w-6 h-6">
              </div>
              <span class="text-2xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                OilBuddy
              </span>
              <span class="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent text-sm font-semibold px-2 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                AI
              </span>
            </router-link>
          </div>

          <!-- Navigation Links -->
          <nav class="hidden md:flex items-center space-x-8">
            <router-link to="/" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Home</router-link>
            <router-link to="/blog" class="text-emerald-400 hover:text-emerald-300 transition-all duration-300 font-medium">Blog</router-link>
            <a href="/#/login" class="text-slate-300 hover:text-emerald-400 font-semibold transition-all duration-300">Sign In</a>
            <router-link to="/register" class="btn-primary-modern">
              Get Started Free
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </router-link>
          </nav>

          <!-- Mobile Menu Button -->
          <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden p-2 text-slate-300 hover:text-emerald-400 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="md:hidden py-4 border-t border-slate-700/50">
          <div class="space-y-2">
            <router-link to="/" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Home</router-link>
            <router-link to="/blog" class="block px-3 py-3 text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Blog</router-link>
            <a href="/#/login" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Sign In</a>
            <router-link to="/register" class="block px-3 py-3 btn-primary-modern mt-2">Get Started Free</router-link>
          </div>
        </div>
      </div>
    </header>

    <div class="pt-20 min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
      <!-- Animated Background -->
      <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
      <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
      
      <!-- Floating Particles -->
      <div class="fixed inset-0 pointer-events-none">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>

      <div class="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Loading State -->
        <div v-if="isLoading" class="animate-pulse">
          <div class="h-4 bg-slate-600/50 rounded w-1/4 mb-4"></div>
          <div class="h-8 bg-slate-600/50 rounded w-3/4 mb-6"></div>
          <div class="h-4 bg-slate-600/50 rounded w-1/2 mb-8"></div>
          <div class="space-y-4">
            <div class="h-4 bg-slate-600/50 rounded"></div>
            <div class="h-4 bg-slate-600/50 rounded"></div>
            <div class="h-4 bg-slate-600/50 rounded w-5/6"></div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="text-red-400 text-lg mb-4">{{ error }}</div>
          <button @click="loadBlogPost" class="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl transition-colors">
            Try Again
          </button>
        </div>

        <!-- Blog Post Content -->
        <article v-else-if="post" class="bg-white/5 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8">
          <!-- Back to Blog Button -->
          <div class="mb-6">
            <router-link to="/blog" class="inline-flex items-center text-emerald-400 hover:text-emerald-300 transition-colors">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Back to Blog
            </router-link>
          </div>

          <!-- Post Header -->
          <header class="mb-8">
            <div class="flex items-center mb-4">
              <span :class="getCategoryClass(post.category)" class="px-3 py-1 rounded-full text-sm font-medium">
                {{ post.category }}
              </span>
              <span class="text-slate-400 text-sm ml-4">{{ formatDate(post.created_at) }}</span>
            </div>
            
            <h1 class="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              {{ post.title }}
            </h1>
            
            <p class="text-xl text-slate-300 mb-6 leading-relaxed">
              {{ post.excerpt }}
            </p>
            
            <div class="flex items-center">
              <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                {{ post.author.charAt(0) }}
              </div>
              <div>
                <div class="text-white font-medium">{{ post.author }}</div>
                <div class="text-slate-400 text-sm">Author</div>
              </div>
            </div>
          </header>

          <!-- Post Content -->
          <div class="prose prose-invert prose-emerald max-w-none">
            <div v-html="formatContent(post.content)" class="text-slate-300 leading-relaxed space-y-6"></div>
          </div>

          <!-- Share Section -->
          <div class="mt-12 pt-8 border-t border-slate-700/50">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-white mb-2">Share this post</h3>
                <p class="text-slate-400 text-sm">Help others discover this content</p>
              </div>
              <div class="flex items-center space-x-4">
                <button @click="sharePost('twitter')" class="p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-xl transition-colors">
                  <svg class="w-5 h-5 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                  </svg>
                </button>
                <button @click="sharePost('linkedin')" class="p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-xl transition-colors">
                  <svg class="w-5 h-5 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                </button>
                <button @click="copyLink" class="p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-xl transition-colors">
                  <svg class="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </article>

        <!-- Related Posts -->
        <div v-if="post && !isLoading" class="mt-12">
          <h3 class="text-2xl font-bold text-white mb-6">More from our blog</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <router-link to="/blog" class="bg-white/5 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/30 transition-all duration-300 group">
              <div class="text-emerald-400 font-medium mb-2 group-hover:text-emerald-300">Explore All Posts</div>
              <p class="text-slate-300">Discover more insights and updates from the OilBuddy AI team</p>
              <svg class="w-5 h-5 text-emerald-400 mt-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { blogService, type BlogPost } from '@/services/blogService'

// State
const route = useRoute()
const mobileMenuOpen = ref(false)
const post = ref<BlogPost | null>(null)
const isLoading = ref(true)
const error = ref<string | null>(null)

// Methods
const getCategoryClass = (category: string) => {
  switch (category.toLowerCase()) {
    case 'product':
      return 'bg-emerald-500/20 text-emerald-400'
    case 'industry':
      return 'bg-cyan-500/20 text-cyan-400'
    case 'tips':
      return 'bg-amber-500/20 text-amber-400'
    case 'updates':
      return 'bg-purple-500/20 text-purple-400'
    default:
      return 'bg-slate-500/20 text-slate-400'
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

const formatContent = (content: string) => {
  // Convert line breaks to paragraphs and handle basic formatting
  return content
    .split('\n\n')
    .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('')
}

const sharePost = (platform: string) => {
  const url = window.location.href
  const title = post.value?.title || 'OilBuddy AI Blog Post'
  
  let shareUrl = ''
  switch (platform) {
    case 'twitter':
      shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
      break
    case 'linkedin':
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
      break
  }
  
  if (shareUrl) {
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }
}

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)
    // You could show a toast notification here
    console.log('Link copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy link:', err)
  }
}

const loadBlogPost = async () => {
  try {
    isLoading.value = true
    error.value = null
    
    const postId = parseInt(route.params.id as string)
    if (isNaN(postId)) {
      throw new Error('Invalid post ID')
    }
    
    const blogPost = await blogService.getBlogPost(postId)
    post.value = blogPost
  } catch (err) {
    console.error('Error loading blog post:', err)
    error.value = 'Failed to load blog post. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadBlogPost()
})
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Header button styles */
.btn-primary-modern {
  @apply inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-2xl hover:from-emerald-400 hover:to-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

/* Prose styles for blog content */
.prose p {
  @apply mb-4 text-slate-300 leading-relaxed;
}

.prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
  @apply text-white font-bold mb-4 mt-8;
}

.prose h1 { @apply text-2xl; }
.prose h2 { @apply text-xl; }
.prose h3 { @apply text-lg; }

.prose ul, .prose ol {
  @apply mb-4 pl-6;
}

.prose li {
  @apply mb-2 text-slate-300;
}

.prose blockquote {
  @apply border-l-4 border-emerald-500 pl-4 italic text-slate-400 my-6;
}

.prose code {
  @apply bg-slate-800 text-emerald-400 px-2 py-1 rounded text-sm;
}

.prose pre {
  @apply bg-slate-800 p-4 rounded-lg overflow-x-auto my-6;
}

.prose a {
  @apply text-emerald-400 hover:text-emerald-300 transition-colors;
}
</style>
